import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { StatCard } from '@/modules/ezparcel/components/dashboard/StatCard';
import { RecentShipments } from '@/modules/ezparcel/components/dashboard/RecentShipments';
import { ActiveRoutes } from '@/modules/ezparcel/components/dashboard/ActiveRoutes';
import { Package, TruckIcon, CheckCircle2, Users, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line } from
'recharts';

const API_URL = API_BASE;

export const Dashboard = () => {const { t } = useTranslation("ezparcel");
  const [stats, setStats] = useState({
    totalShipments: 0,
    todayShipments: 0,
    todayShipmentsChange: 0,
    activeRoutes: 0,
    activeRoutesChange: 0,
    onTimeDeliveries: 95,
    onTimeDeliveriesChange: 2,
    activeDrivers: 0,
    activeDriversChange: 0,
    inTransit: 0,
    delivered: 0,
    pendingPickups: 0,
    outForDelivery: 0,
    failed: 0
  });
  const [shipmentChartData, setShipmentChartData] = useState([]);
  const [performanceChartData, setPerformanceChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('ezerpToken');
        const headers = { 'Authorization': `Bearer ${token}` };

        const [dispatchRes, shipmentsChartRes, performanceChartRes] = await Promise.all([
        fetch(`${API_URL}/api/ezparcel/dispatch-board`, { headers }),
        fetch(`${API_URL}/api/ezparcel/reports/chart-data/shipments`, { headers }),
        fetch(`${API_URL}/api/ezparcel/reports/chart-data/performance`, { headers })]
        );

        if (dispatchRes.ok) {
          const data = await dispatchRes.json();
          const pendingPickups = data.stats?.pending_pickups || 0;
          const inTransit = data.stats?.in_transit || 0;
          const deliveredToday = data.stats?.delivered_today || 0;
          const driversOnRoute = data.stats?.drivers_on_route || 0;

          setStats({
            totalShipments: pendingPickups + inTransit + deliveredToday,
            todayShipments: pendingPickups + inTransit + deliveredToday,
            todayShipmentsChange: 12,
            activeRoutes: data.routes?.length || inTransit,
            activeRoutesChange: 5,
            onTimeDeliveries: 95,
            onTimeDeliveriesChange: 2,
            activeDrivers: driversOnRoute,
            activeDriversChange: 1,
            inTransit: inTransit,
            delivered: deliveredToday,
            pendingPickups: pendingPickups,
            outForDelivery: Math.floor(inTransit * 0.6),
            failed: Math.floor(deliveredToday * 0.02)
          });
        }

        if (shipmentsChartRes.ok) setShipmentChartData(await shipmentsChartRes.json());
        if (performanceChartRes.ok) setPerformanceChartData(await performanceChartRes.json());
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>);

  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("dashboard", "Dashboard")}</h1>
        <p className="text-muted-foreground mt-2">{t("welcomeBackHeres", "Welcome back! Here's what's happening with your logistics operations today.")}

        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Today's Shipments"
          value={(stats.todayShipments ?? 0).toLocaleString()}
          change={stats.todayShipmentsChange}
          trend="up"
          icon={Package} />
        
        <StatCard
          title="Active Routes"
          value={stats.activeRoutes}
          change={stats.activeRoutesChange}
          trend="up"
          icon={TruckIcon} />
        
        <StatCard
          title="On-Time Delivery"
          value={`${stats.onTimeDeliveries}%`}
          change={stats.onTimeDeliveriesChange}
          trend="up"
          icon={CheckCircle2} />
        
        <StatCard
          title="Active Drivers"
          value={stats.activeDrivers}
          change={Math.abs(stats.activeDriversChange)}
          trend="down"
          icon={Users} />
        
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("weeklyShipmentVolume", "Weekly Shipment Volume")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={shipmentChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} />
                
                <Bar dataKey="shipments" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("deliveryPerformanceTrend", "Delivery Performance Trend")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} />
                
                <Line
                  type="monotone"
                  dataKey="onTime"
                  stroke="hsl(var(--success))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--success))' }} />
                
                <Line
                  type="monotone"
                  dataKey="late"
                  stroke="hsl(var(--destructive))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--destructive))' }} />
                
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <RecentShipments />
        <ActiveRoutes />
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("shipmentStatus", "Shipment Status")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("pendingPickup", "Pending Pickup")}</span>
                <span className="font-medium">{stats.pendingPickups}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("inTransit", "In Transit")}</span>
                <span className="font-medium">{stats.inTransit}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("outForDelivery", "Out for Delivery")}</span>
                <span className="font-medium">{stats.outForDelivery}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-green-600">{t("delivered", "Delivered")}</span>
                <span className="font-medium text-green-600">{stats.delivered}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-600">{t("failed", "Failed")}</span>
                <span className="font-medium text-red-600">{stats.failed}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("quickActions", "Quick Actions")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <button className="w-full py-2 px-3 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors text-left">{t("createNewShipment", "Create New Shipment")}

              </button>
              <button className="w-full py-2 px-3 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors text-left">{t("planRoute", "Plan Route")}

              </button>
              <button className="w-full py-2 px-3 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors text-left">{t("generateManifest", "Generate Manifest")}

              </button>
              <button className="w-full py-2 px-3 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors text-left">{t("viewReports", "View Reports")}

              </button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("systemHealth", "System Health")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("apiStatus", "API Status")}</span>
                <span className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <div className="h-2 w-2 rounded-full bg-green-600" />{t("operational", "Operational")}

                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("database", "Database")}</span>
                <span className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <div className="h-2 w-2 rounded-full bg-green-600" />{t("healthy", "Healthy")}

                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("gpsTracking", "GPS Tracking")}</span>
                <span className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <div className="h-2 w-2 rounded-full bg-green-600" />{t("active", "Active")}

                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>);

};